// Hot Works — trigger question sets for the welding, cutting/grinding and
// torch-on roofing reports. Keyed by report slug (see manifest). Mirrors the
// tone and structure of launch.ts.

import type { TriggerQuestion } from "./types";
import {
  SILICA,
  HAVS,
  CONFINED_SPACE,
  FRAGILE_ROOF,
  FALLING_OBJECTS,
  ACCESS_OPTIONS,
  HOT_WORK_PERMIT_Q,
  COSHH_DETAIL_QS,
  RESCUE_Q,
} from "./shared";

// Hazard ids minted in this batch's report JSONs (no shared constant exists).
const BITUMEN_FUME = "haz-dust-fumes-bitumen-fume-exposure";
const NOISE = "haz-noise-vibration-noise-exposure-above-action-value";
const GAS_CYLINDER = "haz-hot-works-compressed-ga-cylinder-leak-flashback-or-fall";

export const HOT_WORKS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  welding: [
    HOT_WORK_PERMIT_Q,
    {
      key: "gasWelding",
      label: "Is oxy-fuel / gas welding used (compressed gas cylinders)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [GAS_CYLINDER], scopeLine: "Oxy-fuel gas welding in use — cylinders moved on a trolley, secured upright, with flashback arrestors and non-return valves fitted and joints leak-tested." },
        no: {
          hazardsOff: [GAS_CYLINDER],
          emergencyOff: ["Gas cylinder leak"],
          scopeLine: "No oxy-fuel / gas cylinders — arc welding only; compressed-gas cylinder controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [GAS_CYLINDER], failCheck: "Gas cylinder use confirmed" },
    },
    {
      key: "confined",
      label: "Will any welding be inside a tank, vessel, void or other enclosed space?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [CONFINED_SPACE], permitsAdd: ["Confined space entry permit"] },
        no: {
          hazardsOff: [CONFINED_SPACE],
          permitsRemove: ["Confined space entry permit"],
          emergencyOff: ["Welder overcome"],
          sequenceOff: [5],
          scopeLine: "No welding inside tanks, vessels or enclosed spaces — confined space entry controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [CONFINED_SPACE], permitsAdd: ["Confined space entry permit"], failCheck: "Confined-space welding confirmed" },
    },
    {
      key: "noise",
      label: "Will chipping, grinding or gouging accompany the welding (noise)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [NOISE] },
        no: { hazardsOff: [NOISE], scopeLine: "No significant chipping, grinding or gouging — noise is below the action values for this task." },
      },
      notSure: { hazardsOn: [NOISE], failCheck: "Noise exposure confirmed" },
    },
    {
      key: "fumeControl",
      label: "Is LEV / on-tool extraction provided for ALL welding (including mild steel), with RPE as a supplement?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "LEV / on-tool extraction provided at the weld point for all welding, with face-fitted RPE as a supplement; LEV on a thorough-examination schedule." },
        no: { failCheck: "LEV provided for all welding (RPE alone is not acceptable post-2019)" },
      },
      notSure: { failCheck: "LEV provided for all welding (RPE alone is not acceptable post-2019)" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "fireWatch",
      label: "Who holds the fire watch and for how long after welding stops?",
      kind: "text",
      placeholder: "e.g. labourer holds 60-min watch, then hourly checks",
      requiredCheck: "Fire watch owner and duration recorded",
      effects: { "*": { scopeLine: "Fire watch: {answer}." } },
    },
  ],

  "cutting-grinding": [
    HOT_WORK_PERMIT_Q,
    {
      key: "abrasiveWheelTrained",
      label: "Is the named operative trained and competent to mount and change abrasive wheels (PUWER)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Operative holds current abrasive wheels training (PUWER) with demonstrated practical competence; wheel rpm matched to the tool." },
        no: { failCheck: "Abrasive wheels training / competence confirmed for the named operative" },
      },
      notSure: { failCheck: "Abrasive wheels training / competence confirmed for the named operative" },
    },
    {
      key: "masonry",
      label: "Will masonry, paving or concrete be cut (respirable crystalline silica)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA] },
        no: {
          hazardsOff: [SILICA],
          substancesOff: ["Respirable crystalline silica"],
          emergencyOff: ["Dust over-exposure"],
          scopeLine: "No cutting of masonry, paving or concrete — respirable crystalline silica controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "Silica-generating cutting confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "dustControl",
            label: "How is silica dust controlled at source?",
            kind: "choice",
            options: ["On-tool extraction (M/H-class)", "Water suppression (wet cutting)", "Both extraction and water"],
            effects: {
              "On-tool extraction (M/H-class)": { scopeLine: "Silica controlled at source by M/H-class on-tool extraction, with face-fitted FFP3 RPE as a supplement." },
              "Water suppression (wet cutting)": { scopeLine: "Silica controlled at source by water suppression / wet cutting, with face-fitted FFP3 RPE as a supplement." },
              "Both extraction and water": { scopeLine: "Silica controlled at source by both on-tool extraction and water suppression, with face-fitted FFP3 RPE as a supplement." },
            },
          },
          ...COSHH_DETAIL_QS,
        ],
      },
    },
    {
      key: "havs",
      label: "Is grinder/saw use prolonged enough to risk hand-arm vibration (HAVS)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [HAVS] },
        no: { hazardsOff: [HAVS], scopeLine: "Cutting/grinding is short-duration and within the daily HAVS trigger time — extended vibration controls are not required for this task." },
      },
      notSure: { hazardsOn: [HAVS], failCheck: "HAVS exposure confirmed" },
    },
    {
      key: "noise",
      label: "Will cutting/grinding exceed the noise action values?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [NOISE] },
        no: { hazardsOff: [NOISE], scopeLine: "Cutting/grinding is brief and below the noise action values for this task." },
      },
      notSure: { hazardsOn: [NOISE], failCheck: "Noise exposure confirmed" },
    },
    {
      key: "fireWatch",
      label: "Who holds the fire watch and for how long after cutting stops?",
      kind: "text",
      placeholder: "e.g. operative holds 60-min watch, voids/drains re-checked",
      requiredCheck: "Fire watch owner and duration recorded",
      effects: { "*": { scopeLine: "Fire watch: {answer}." } },
    },
  ],

  "torch-on-roofing": [
    HOT_WORK_PERMIT_Q,
    {
      key: "safe2torch",
      label: "Has a Safe2Torch survey been done and torch-free zones marked on the drawings?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Safe2Torch survey completed; torch-free zones marked on the drawings and formed with hot-air or cold-applied methods." },
        no: { failCheck: "Safe2Torch survey and torch-free zoning confirmed" },
      },
      notSure: { failCheck: "Safe2Torch survey and torch-free zoning confirmed" },
    },
    {
      key: "fragile",
      label: "Are fragile surfaces, rooflights or weakened decking present?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FRAGILE_ROOF] },
        no: { hazardsOff: [FRAGILE_ROOF], scopeLine: "No fragile surfaces, rooflights or weakened decking on or near the work area." },
      },
      notSure: { hazardsOn: [FRAGILE_ROOF], failCheck: "Fragile surfaces / rooflights confirmed" },
    },
    {
      key: "publicBelow",
      label: "Are occupants, the public or other trades below or nearby?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FALLING_OBJECTS], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [FALLING_OBJECTS], emergencyOff: ["Person struck"], scopeLine: "No occupants, public or other trades below or adjacent — falling-object exclusion below the roof is not required for this task." },
      },
      notSure: { hazardsOn: [FALLING_OBJECTS], failCheck: "People below / nearby confirmed" },
    },
    {
      key: "bitumenFume",
      label: "Is the work in a sheltered or enclosed area where bitumen fume could build up?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [BITUMEN_FUME] },
        no: {
          hazardsOff: [BITUMEN_FUME],
          substancesOff: ["Bitumen fumes"],
          scopeLine: "Open-air roof with good natural ventilation — bitumen fume is dispersed; additional fume controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [BITUMEN_FUME], failCheck: "Bitumen fume build-up confirmed" },
      followUps: { when: "yes", questions: COSHH_DETAIL_QS },
    },
    {
      key: "accessMethod",
      label: "What access and edge protection will be used?",
      kind: "choice",
      options: ACCESS_OPTIONS,
      effects: Object.fromEntries(ACCESS_OPTIONS.map((o) => [o, { scopeLine: `Roof access / edge protection: ${o}.` }])),
    },
    RESCUE_Q,
  ],
};
